/*       */

import React, {useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { Button as PuiButton, ConfirmationModal, MdiIcon } from '@mic3/platform-ui';

import history from 'store/History';
import { useToggle } from 'app/utils/hook/hooks';
import { appendHttp } from 'app/utils/string/string-utils';

import FormField from 'app/containers/Designer/Form/components/FormField';
import ModalDialog from 'app/components/organisms/ModalDialog/ModalDialog';

const fieldify = Component => props => <FormField {...props} Component={Component} />;

const PuiButtonStyled = styled(PuiButton)`
    text-align: center;
    text-decoration: none;
    &:hover {
        text-decoration: none !important;
    }
`;

const ConfirmationModalFildifyed = fieldify(ConfirmationModal);

const ModalWrapper = styled.div`
    ${({ position }) => position && `position: ${position} !important;`}
`;

const ModalDialogStyled = styled(ModalDialog)`
    & .MuiDialog-paper {
        height: ${({ height }) => (height ? height : window.innerHeight)}px;
        width: ${({ width }) => (width ? width : window.innerWidth)}px;
    }
    & .MuiDialog-paperWidthMd {
        max-width: ${({ width }) => (width ? width : window.innerWidth)}px;
    }
`;

const Button = ({
    withConfirmation,
    confirmationModalProps,
    to,
    onClick,
    onClose,
    onBeforeClick,
    afterAction,
    afterActionType,
    afterActionTo,
    afterActionHref,
    afterActionFunction,
    validateBefore,
    isValidForm,
    href,
    target,
    actionType,
    modalWithWrapper,
    modalWrapperProps,
    newWindowLink,
    dialogLink,
    windowHeight,
    windowWidth,
    dialogHeight,
    dialogWidth,
    icon,
    ...buttonProps
}) => {
    const [openConfirmationModal, toggleConfirmationModal, handleConfirmationModal] = useToggle();
    const [isOpenDialog, setDialogOpen] = useState(false)

    const getDimensions = (desiredWidth, desiredHeight) => {
        const width = desiredWidth && desiredWidth <= window.innerWidth ? desiredWidth : window.innerWidth;
        const height = desiredHeight && desiredHeight <= window.innerHeight ? desiredHeight : window.innerHeight;
        return { width, height };
    };

    const openDialog = (dialogUrl) => {
        const { width, height } = getDimensions(dialogWidth, dialogHeight);
        const validUrl = appendHttp(dialogUrl)

        return (
            <ModalDialogStyled 
                onClose={() => setDialogOpen(false)}
                width={width} height={height}>
                <iframe src={validUrl} title="Dialog Content" style={{ width: '100%', height: `calc(${height}px - ${height * 0.16}px)`, overflow: 'visible' }}></iframe>
            </ModalDialogStyled>
        );
    }
    const openNewWindow = useCallback((url) =>{
        const { width, height } = getDimensions(windowWidth, windowHeight);
        const validUrl = appendHttp(url)
        const left = Math.floor((window.innerWidth - width) / 2);
        const top = Math.floor((window.innerHeight - height) / 2);
        window.open(validUrl, 'newWindow', `width=${width},height=${height},top=${top},left=${left},scrollbars=yes,noopener,popup`);
    }, [windowWidth, windowHeight]);


    const handleOnClick = useMemo(() => {
        if (to && actionType === 'internal') {
            return () => {
                history.push(to);
            };
        }
        if ((to || href) && actionType === 'external') {
            return () => {
                const validUrl = appendHttp(to || href);
                if (target === '_blank') {
                    window.open(to || href, target);
                }else if(target === 'dialog'){
                    setDialogOpen(true);
                }else if(target === 'newWindow'){
                    openNewWindow(validUrl);
                }else {
                    window.location.href = validUrl;
                }
            };
        }
        return onClick;
    }, [actionType, to, href, onClick, target, openNewWindow]);
    const handleOnClose = useCallback(
        e => {
            handleConfirmationModal(false);
            onClose && onClose(e);
        },
        [onClose, handleConfirmationModal]
    );
    const onClickWithConfirm = withConfirmation ? toggleConfirmationModal : handleOnClick;
    const validateOnClick = useCallback(
        async event => {
            if (event && event.persist) {
                event.persist();
            }
            const { errors } = await isValidForm();
            if (!errors) {
                if (withConfirmation) {
                    toggleConfirmationModal();
                } else {
                    handleOnClick(event);
                }
            }
        },
        [isValidForm, withConfirmation, handleOnClick, toggleConfirmationModal]
    );
    const onClickAction = validateBefore ? validateOnClick : onClickWithConfirm;
    
    return (
        <>
            <PuiButtonStyled {...buttonProps} onClick={onClickAction} startIcon={icon && <MdiIcon name={icon?.value} type={icon?.type} />} />
            {withConfirmation &&
                confirmationModalProps &&
                (modalWithWrapper ? (
                    <ModalWrapper {...modalWrapperProps}>
                        <ConfirmationModalFildifyed
                            {...confirmationModalProps}
                            onClose={handleOnClose}
                            open={openConfirmationModal}
                            onConfirm={(e) => {
                                if (onBeforeClick) onBeforeClick();
                                return handleOnClick(e);
                            }}
                        />
                    </ModalWrapper>
                ) : (
                    <ConfirmationModalFildifyed
                        {...confirmationModalProps}
                        onClose={handleOnClose}
                        open={openConfirmationModal}
                        onConfirm={(e) => {
                            if (onBeforeClick) onBeforeClick();
                            return handleOnClick(e);
                        }}
                    />
                ))}
            {isOpenDialog && openDialog(href)}
        </>
    );
};

export default Button;
